import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Camera, Plus, Trash2, Sparkles,
  Clock, Users, ChevronDown, ChevronUp, Loader2, Check, Upload
} from 'lucide-react';
import { foodCommunityService, Ingredient } from '../services/foodCommunityService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const DIET_TAGS = [
  { value: 'HIGH_PROTEIN', label: '#highprotein' },
  { value: 'LOW_CALORIE', label: '#lowcalorie' },
  { value: 'LOW_CARB', label: '#lowcarb' },
  { value: 'VEGAN', label: '#vegan' },
  { value: 'VEGETARIAN', label: '#vegetarian' },
  { value: 'GLUTEN_FREE', label: '#faragluten' },
  { value: 'DAIRY_FREE', label: '#faralactate' },
  { value: 'ANTI_INFLAMMATORY', label: '#antiinflamator' },
  { value: 'WEIGHT_LOSS', label: '#slabit' },
  { value: 'MUSCLE_GAIN', label: '#masamusculara' },
  { value: 'KETO', label: '#keto' },
  { value: 'MEDITERRANEAN', label: '#mediteranean' },
  { value: 'HIGH_FIBER', label: '#highfiber' },
  { value: 'LOW_FAT', label: '#lowfat' },
  { value: 'BALANCED', label: '#echilibrat' },
];

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Mic dejun' },
  { value: 'lunch', label: 'Pranz' },
  { value: 'dinner', label: 'Cina' },
  { value: 'snack', label: 'Gustare' },
];

interface NutritionData {
  calories?: number;
  protein?: number;
  carbs?: number;
  fats?: number;
}

const CreateRecipePostPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [mealType, setMealType] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [servings, setServings] = useState('1');

  const [ingredients, setIngredients] = useState<Ingredient[]>([
    { name: '', amount: '', unit: 'g' }
  ]);
  const [instructions, setInstructions] = useState('');

  const [nutrition, setNutrition] = useState<NutritionData>({});
  const [analyzingNutrition, setAnalyzingNutrition] = useState(false);
  const [nutritionAnalyzed, setNutritionAnalyzed] = useState(false);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState('');
  const [customTags, setCustomTags] = useState<string[]>([]);

  const [showNutrition, setShowNutrition] = useState(true);
  const [showIngredients, setShowIngredients] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Upload image
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setImageUrl(data.url || data.imageUrl || '');
        // Auto-analyze nutrition with Food Vision AI
        if (data.url || data.imageUrl) {
          analyzeImageNutrition(data.url || data.imageUrl);
        }
      } else {
        // Use preview as base64 if upload fails
        setImageUrl(imagePreview);
      }
    } catch {
      setImageUrl(imagePreview);
    } finally {
      setUploadingImage(false);
    }
  };

  // Analyze nutrition with Food Vision AI
  const analyzeImageNutrition = async (imgUrl: string) => {
    setAnalyzingNutrition(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/api/food-tracking/analyze-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ imageUrl: imgUrl }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.nutrition || data.calories) {
          const n = data.nutrition || data;
          setNutrition({
            calories: n.calories || n.kcal,
            protein: n.protein,
            carbs: n.carbs || n.carbohydrates,
            fats: n.fats || n.fat,
          });
          setNutritionAnalyzed(true);
          // Auto-suggest title if empty
          if (!title && data.name) setTitle(data.name);
          // Auto-suggest ingredients if available
          if (data.ingredients?.length && ingredients.every(i => !i.name)) {
            setIngredients(data.ingredients.map((ing: any) => ({
              name: typeof ing === 'string' ? ing : ing.name || '',
              amount: ing.amount || '',
              unit: ing.unit || 'g',
            })));
          }
        }
      }
    } catch {}
    finally {
      setAnalyzingNutrition(false);
    }
  };

  const handleAddIngredient = () => {
    setIngredients(prev => [...prev, { name: '', amount: '', unit: 'g' }]);
  };

  const handleRemoveIngredient = (idx: number) => {
    setIngredients(prev => prev.filter((_, i) => i !== idx));
  };

  const handleIngredientChange = (idx: number, field: keyof Ingredient, value: string) => {
    setIngredients(prev => prev.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing));
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleAddCustomTag = () => {
    const tag = customTag.trim().replace(/^#/, '').toLowerCase();
    if (tag && !customTags.includes(tag)) {
      setCustomTags(prev => [...prev, tag]);
      setCustomTag('');
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Titlul retetei este obligatoriu');
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      const validIngredients = ingredients.filter(i => i.name.trim());
      const post = await foodCommunityService.createPost({
        title: title.trim(),
        description: description.trim() || undefined,
        imageUrl: imageUrl || undefined,
        prepTime: prepTime ? parseInt(prepTime) : undefined,
        servings: servings ? parseInt(servings) : 1,
        calories: nutrition.calories,
        protein: nutrition.protein,
        carbs: nutrition.carbs,
        fats: nutrition.fats,
        ingredients: validIngredients.length > 0 ? validIngredients : undefined,
        instructions: instructions.trim() || undefined,
        tags: selectedTags,
        customTags,
        mealType: mealType || undefined,
      });
      navigate(`/food-community/post/${post.id}`, { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'A aparut o eroare. Incearca din nou.');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = title.trim().length > 0 && !submitting;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </button>
          <h1 className="font-bold text-gray-900">Adauga reteta</h1>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              canSubmit
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : 'Publica'}
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 pb-24 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Image Upload */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {imagePreview ? (
            <div className="relative">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full object-cover"
                style={{ maxHeight: 300 }}
              />
              {uploadingImage && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="flex items-center gap-2 text-white text-sm">
                    <Loader2 size={18} className="animate-spin" />
                    Se incarca...
                  </div>
                </div>
              )}
              {analyzingNutrition && (
                <div className="absolute bottom-3 left-3 right-3 bg-black/70 text-white text-xs px-3 py-2 rounded-xl flex items-center gap-2">
                  <Sparkles size={13} className="text-green-400" />
                  Food Vision AI analizeaza nutritia...
                </div>
              )}
              {nutritionAnalyzed && !analyzingNutrition && (
                <div className="absolute bottom-3 left-3 bg-green-600 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
                  <Check size={12} />
                  Nutritie completata de AI
                </div>
              )}
              <button
                onClick={() => { setImagePreview(''); setImageUrl(''); setNutritionAnalyzed(false); }}
                className="absolute top-3 right-3 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ) : (
            <div className="p-4">
              <p className="text-xs text-gray-400 text-center mb-3">
                Poza va fi analizata automat de AI • JPG, PNG, WebP, GIF acceptate
              </p>
              <div className="grid grid-cols-2 gap-3">
                {/* Camera button – uses label for reliable iOS trigger */}
                <label
                  htmlFor="food-camera-input"
                  className="flex flex-col items-center justify-center gap-2 py-5 border-2 border-dashed border-green-300 rounded-xl bg-green-50 active:bg-green-100 transition-colors cursor-pointer"
                >
                  <Camera size={22} className="text-green-600" />
                  <span className="text-sm font-medium text-green-700">Fă o poză</span>
                </label>
                {/* Gallery button – uses label for reliable iOS trigger */}
                <label
                  htmlFor="food-gallery-input"
                  className="flex flex-col items-center justify-center gap-2 py-5 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
                >
                  <Upload size={22} className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">Din galerie</span>
                </label>
              </div>
            </div>
          )}
          {/* Gallery input – NO capture attribute → opens photo library on iOS/Android */}
          <input
            id="food-gallery-input"
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/heic,image/heif"
            className="hidden"
            onChange={handleImageSelect}
          />
          {/* Camera input – capture="environment" → opens camera directly */}
          <input
            id="food-camera-input"
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleImageSelect}
          />
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <input
            type="text"
            placeholder="Titlul retetei *"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full text-lg font-semibold text-gray-900 placeholder-gray-300 border-none outline-none bg-transparent"
            maxLength={100}
          />
          <textarea
            placeholder="Descrie reteta ta (optional)..."
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
            className="w-full text-sm text-gray-600 placeholder-gray-300 border-none outline-none bg-transparent resize-none"
            maxLength={500}
          />

          {/* Meal Type + Time + Servings */}
          <div className="flex items-center gap-3 pt-1 border-t border-gray-50">
            <select
              value={mealType}
              onChange={e => setMealType(e.target.value)}
              className="flex-1 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 outline-none"
            >
              <option value="">Tip masa</option>
              {MEAL_TYPES.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>

            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
              <Clock size={14} className="text-gray-400" />
              <input
                type="number"
                placeholder="Min"
                value={prepTime}
                onChange={e => setPrepTime(e.target.value)}
                className="w-12 text-sm text-gray-600 bg-transparent outline-none"
                min="1"
                max="999"
              />
              <span className="text-xs text-gray-400">min</span>
            </div>

            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
              <Users size={14} className="text-gray-400" />
              <input
                type="number"
                placeholder="1"
                value={servings}
                onChange={e => setServings(e.target.value)}
                className="w-8 text-sm text-gray-600 bg-transparent outline-none"
                min="1"
                max="20"
              />
              <span className="text-xs text-gray-400">portii</span>
            </div>
          </div>
        </div>

        {/* Nutrition */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setShowNutrition(!showNutrition)}
            className="w-full flex items-center justify-between px-4 py-3"
          >
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-green-600" />
              <span className="font-semibold text-gray-800 text-sm">Valori nutritionale</span>
              {nutritionAnalyzed && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">AI</span>
              )}
            </div>
            {showNutrition ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>

          {showNutrition && (
            <div className="px-4 pb-4 grid grid-cols-2 gap-3">
              {[
                { key: 'calories', label: 'Calorii', unit: 'kcal', color: 'text-orange-600' },
                { key: 'protein', label: 'Proteine', unit: 'g', color: 'text-green-600' },
                { key: 'carbs', label: 'Carbohidrati', unit: 'g', color: 'text-blue-600' },
                { key: 'fats', label: 'Grasimi', unit: 'g', color: 'text-amber-600' },
              ].map(field => (
                <div key={field.key} className="bg-gray-50 rounded-xl p-3">
                  <label className={`text-xs font-medium ${field.color} uppercase tracking-wide`}>
                    {field.label}
                  </label>
                  <div className="flex items-center gap-1 mt-1">
                    <input
                      type="number"
                      placeholder="0"
                      value={(nutrition as any)[field.key] || ''}
                      onChange={e => setNutrition(prev => ({
                        ...prev,
                        [field.key]: e.target.value ? parseFloat(e.target.value) : undefined
                      }))}
                      className="flex-1 text-lg font-bold text-gray-800 bg-transparent outline-none w-full"
                      min="0"
                      step="0.1"
                    />
                    <span className="text-xs text-gray-400">{field.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ingredients */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setShowIngredients(!showIngredients)}
            className="w-full flex items-center justify-between px-4 py-3"
          >
            <span className="font-semibold text-gray-800 text-sm">
              Ingrediente ({ingredients.filter(i => i.name).length})
            </span>
            {showIngredients ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>

          {showIngredients && (
            <div className="px-4 pb-4 space-y-2">
              {ingredients.map((ing, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Ingredient"
                    value={ing.name}
                    onChange={e => handleIngredientChange(idx, 'name', e.target.value)}
                    className="flex-1 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 outline-none focus:border-green-400"
                  />
                  <input
                    type="text"
                    placeholder="100"
                    value={ing.amount}
                    onChange={e => handleIngredientChange(idx, 'amount', e.target.value)}
                    className="w-16 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-xl px-2 py-2 outline-none focus:border-green-400 text-center"
                  />
                  <select
                    value={ing.unit || 'g'}
                    onChange={e => handleIngredientChange(idx, 'unit', e.target.value)}
                    className="w-16 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-xl px-1 py-2 outline-none"
                  >
                    {['g', 'ml', 'kg', 'l', 'buc', 'lingura', 'cana', 'varf'].map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                  {ingredients.length > 1 && (
                    <button
                      onClick={() => handleRemoveIngredient(idx)}
                      className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={handleAddIngredient}
                className="flex items-center gap-2 text-sm text-green-600 font-medium hover:text-green-700 mt-1"
              >
                <Plus size={16} />
                Adauga ingredient
              </button>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <label className="text-sm font-semibold text-gray-800 block mb-2">
            Mod de preparare
          </label>
          <textarea
            placeholder="Descrie pasii de preparare..."
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            rows={5}
            className="w-full text-sm text-gray-600 placeholder-gray-300 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-green-400 resize-none"
          />
        </div>

        {/* Tags */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <label className="text-sm font-semibold text-gray-800 block mb-3">
            Etichete dieta
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {DIET_TAGS.map(tag => (
              <button
                key={tag.value}
                onClick={() => handleTagToggle(tag.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  selectedTags.includes(tag.value)
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tag.label}
              </button>
            ))}
          </div>

          {/* Custom tags */}
          {customTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {customTags.map(tag => (
                <span
                  key={tag}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                >
                  #{tag}
                  <button onClick={() => setCustomTags(prev => prev.filter(t => t !== tag))}>
                    <Trash2 size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Adauga eticheta personalizata..."
              value={customTag}
              onChange={e => setCustomTag(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddCustomTag()}
              className="flex-1 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 outline-none focus:border-green-400"
            />
            <button
              onClick={handleAddCustomTag}
              className="px-3 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full py-4 rounded-2xl text-base font-bold transition-colors ${
            canSubmit
              ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg shadow-green-200'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={18} className="animate-spin" />
              Se publica...
            </span>
          ) : 'Publica reteta'}
        </button>
      </div>
    </div>
  );
};

export default CreateRecipePostPage;
